import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Settings as SettingsIcon,
  Visibility as ViewIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useLoader } from '../../contexts/LoaderContext';
import { useModal } from '../../contexts/ModalContext';

const CustomWebhooks = () => {
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const { openSuccessModal, openErrorModal } = useModal();

  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/lms/integrations/custom-webhooks/');
      setWebhooks(response.data.webhooks || []);
    } catch (error) {
      console.error('Error fetching webhooks:', error);
      openErrorModal('Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWebhook = async () => {
    if (!formData.name.trim()) {
      openErrorModal('Webhook name is required');
      return;
    }

    try {
      showLoader('Creating webhook...');
      const response = await api.post('/lms/integrations/custom-webhooks/', formData);
      
      if (response.data.success) {
        openSuccessModal('Webhook created successfully');
        setCreateDialogOpen(false);
        setFormData({ name: '', description: '', is_active: true });
        
        // Navigate to the webhook detail page for field mapping
        navigate(`/settings/custom-webhooks/${response.data.webhook.id}`);
      }
    } catch (error) {
      openErrorModal(error.response?.data?.error || 'Failed to create webhook');
    } finally {
      hideLoader();
    }
  };

  const handleDeleteWebhook = async () => {
    if (!selectedWebhook) return;

    try {
      showLoader('Deleting webhook...');
      await api.delete(`/lms/integrations/custom-webhooks/${selectedWebhook.id}/`);
      
      openSuccessModal('Webhook deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedWebhook(null);
      fetchWebhooks();
    } catch (error) {
      openErrorModal(error.response?.data?.error || 'Failed to delete webhook');
    } finally {
      hideLoader();
    }
  };

  const handleToggleWebhook = async (webhook) => {
    try {
      showLoader('Updating webhook status...');
      const response = await api.post(`/lms/integrations/custom-webhooks/${webhook.id}/toggle/`);
      
      if (response.data.success) {
        openSuccessModal(response.data.message);
        fetchWebhooks();
      }
    } catch (error) {
      openErrorModal(error.response?.data?.error || 'Failed to toggle webhook');
    } finally {
      hideLoader();
    }
  };

  const handleCopyWebhookUrl = (webhook) => {
    navigator.clipboard.writeText(webhook.webhook_url);
    openSuccessModal('Webhook URL copied to clipboard');
  };

  const getSuccessRate = (webhook) => {
    if (webhook.total_requests === 0) return 0;
    return Math.round((webhook.successful_requests / webhook.total_requests) * 100);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Custom Webhooks
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage custom webhooks with field mapping to receive leads from any source
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Webhook
        </Button>
      </Box>

      {/* Info Alert */}
      {webhooks.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>No webhooks created yet.</strong> Create your first custom webhook to start receiving leads from external sources.
            You can configure field mappings to match your data structure.
          </Typography>
        </Alert>
      )}

      {/* Webhooks Table */}
      {webhooks.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Requests</strong></TableCell>
                <TableCell><strong>Success Rate</strong></TableCell>
                <TableCell><strong>Last Activity</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {webhooks.map((webhook) => (
                <TableRow key={webhook.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {webhook.name}
                    </Typography>
                    {webhook.description && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {webhook.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={webhook.is_active ? <CheckCircleIcon /> : <ErrorIcon />}
                      label={webhook.is_active ? 'Active' : 'Inactive'}
                      color={webhook.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {webhook.total_requests || 0} total
                    </Typography>
                    <Typography variant="caption" color="success.main">
                      {webhook.successful_requests || 0} success
                    </Typography>
                    <Typography variant="caption" color="error.main" sx={{ ml: 1 }}>
                      {webhook.failed_requests || 0} failed
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight="medium">
                        {getSuccessRate(webhook)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {webhook.last_request_at ? (
                      <Typography variant="caption" color="text.secondary">
                        {new Date(webhook.last_request_at).toLocaleString()}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No activity yet
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Copy Webhook URL">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyWebhookUrl(webhook)}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Configure">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/settings/custom-webhooks/${webhook.id}`)}
                      >
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={webhook.is_active ? 'Deactivate' : 'Activate'}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleWebhook(webhook)}
                      >
                        {webhook.is_active ? (
                          <ToggleOnIcon fontSize="small" color="success" />
                        ) : (
                          <ToggleOffIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedWebhook(webhook);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Webhook Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Custom Webhook</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              label="Webhook Name"
              fullWidth
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ mb: 1.5 }}
              size="small"
              helperText="A descriptive name for this webhook (e.g., 'Website Contact Form')"
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              sx={{ mb: 1.5 }}
              size="small"
              helperText="Optional description of what this webhook is for"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateWebhook} variant="contained" color="primary">
            Create & Configure
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Webhook</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the webhook "{selectedWebhook?.name}"?
            This action cannot be undone and all field mappings will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteWebhook} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomWebhooks;
